// src/pages/Longansedit.jsx
import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import '../styles/Longans.css';
import { FaBell, FaUserCircle } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const LOCAL_SELECTED_KEY = 'selectedQuestionsByChapter';
const apiBase = 'https://digital.tifleducation.com/admin/public/api';

const Longansedit = () => {
  const navigate = useNavigate();
  const { paperId } = useParams(); // optional param
  const location = useLocation();
  const state = location.state || {};

  const [activeItem, setActiveItem] = useState('papers');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [user, setUser] = useState({ id: null, name: '', email: '', password: '' });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [longLimit, setLongLimit] = useState(0);

  // -------------------- FETCH DATA --------------------
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return toast.error('User not logged in.');

        // Fetch user info
        const dashRes = await axios.get(`${apiBase}/getDashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (dashRes.data?.user) {
          setUser({
            id: dashRes.data.user.id,
            name: dashRes.data.user.name || '',
            email: dashRes.data.user.email || '',
            password: '',
          });
        }

        // Get selected board/grade/subject
        const boardId = localStorage.getItem('selectedBoardId');
        const gradeId = localStorage.getItem('selectedGradeId');
        const subjectId = localStorage.getItem('selectedSubjectId');
        if (!boardId || !gradeId || !subjectId) return toast.error('Missing selected board/grade/subject.');

        let data = {};
        if (paperId && paperId !== 'new') {
          const res = await axios.get(`${apiBase}/papers/${paperId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          data = res.data?.questions || {};
        } else {
          const res = await axios.post(
            `${apiBase}/papers/fetch-questions`,
            { board: Number(boardId), grade: Number(gradeId), subject: Number(subjectId) },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          data = res.data || {};
        }

        const longQs = [];
        Object.entries(data).forEach(([chapterId, chapterData]) => {
          const types = chapterData.types || {};
          const longArr = types['Long Question Answer'] || [];
          (Array.isArray(longArr) ? longArr : []).forEach(q => {
            longQs.push({ ...q, chapter_id: q.chapter_id ?? Number(chapterId) });
          });
        });
        setQuestions(longQs);

        // Restore selected questions from localStorage
        const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
        const selectedMap = {};
        Object.values(saved).forEach(chapterData => {
          const arr = chapterData.long_qa || [];
          arr.forEach(id => { selectedMap[Number(id)] = true; });
        });
        setSelectedQuestions(selectedMap);

        // Restore limit
        const regularForm = JSON.parse(localStorage.getItem('regularPaperForm') || '{}');
        setLongLimit(Number(regularForm.long || 0));
      } catch (error) {
        console.error('Error fetching long questions:', error.response?.data || error);
        toast.error('Failed to fetch long questions.');
      }
    };

    fetchData();
  }, [paperId]);

  // -------------------- SELECT QUESTIONS --------------------
  const handleSelectQuestion = async (qId, chapterId) => {
    const alreadySelected = !!selectedQuestions[qId];
    const currentCount = Object.keys(selectedQuestions).length;

    if (!alreadySelected && !longLimit) return toast.error('No long ans limit set.');
    if (!alreadySelected && currentCount >= longLimit) return toast.error(`You can only select up to ${longLimit} long questions.`);

    const nextSelected = { ...selectedQuestions };
    if (nextSelected[qId]) delete nextSelected[qId];
    else nextSelected[qId] = true;
    setSelectedQuestions(nextSelected);

    try {
      const saved = JSON.parse(localStorage.getItem(LOCAL_SELECTED_KEY) || '{}');
      const chapterKey = String(chapterId);
      if (!saved[chapterKey]) saved[chapterKey] = {};
      if (!Array.isArray(saved[chapterKey].long_qa)) saved[chapterKey].long_qa = [];

      const arr = saved[chapterKey].long_qa;
      const idx = arr.findIndex(x => Number(x) === Number(qId));
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(Number(qId));
      saved[chapterKey].long_qa = arr;

      localStorage.setItem(LOCAL_SELECTED_KEY, JSON.stringify(saved));

      // API sync only if editing existing paper
      if (paperId && paperId !== 'new') {
        const token = localStorage.getItem('token');
        await axios.put(
          `${apiBase}/papers/${paperId}`,
          { selectedQuestions: saved },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      toast.success('Selection saved');
    } catch (error) {
      console.error('Error saving long selection:', error.response?.data || error);
      toast.error('Failed to save selection');
    }
  };

  // -------------------- PROFILE SAVE --------------------
  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${apiBase}/update-profile`,
        { name: user.name, password: user.password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Profile updated successfully!');
      setIsModalOpen(false);
      setUser({ ...user, password: '' });
    } catch (error) {
      console.error('Error updating profile:', error.response?.data || error);
      toast.error('Failed to update profile');
    }
  };

  // -------------------- RENDER --------------------
  return (
    <div className="dashboard-container">
      <Sidebar
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        activeItem={activeItem}
        setActiveItem={setActiveItem}
      />

      <main className="main-content">
        <div className="long-header">
          <div className="long-welcome-text">
            <a onClick={() => navigate(-1)} className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true"></i>
            </a>
            <span className="long-welcome-text">Long Questions</span>
          </div>

          <div className="header-icons" ref={dropdownRef}>
            <FaBell className="icon" />
            <FaUserCircle className="icon" onClick={() => setIsModalOpen(true)} />
          </div>
        </div>

        <div className="long-questions-container">
          {questions.length > 0 ? (
            questions.map((q, index) => (
              <div key={q.id} className="long-question-box">
                <div className="long-question">
                  <input
                    type="checkbox"
                    className="long-checkbox"
                    checked={!!selectedQuestions[q.id]}
                    onChange={() => handleSelectQuestion(q.id, q.chapter_id)}
                    disabled={!selectedQuestions[q.id] && Object.keys(selectedQuestions).length >= longLimit}
                  />
                  <span className="question-text">
                    {index + 1}. {String(q.question || q.question_title || '').replace(/<\/?[^>]+(>|$)/g, '')}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="no-questions-container">
              <div className="no-questions-icon">📄</div>
              <h3>No Long Questions Found</h3>
              <p style={{ color: '#666' }}>
                There are currently no long questions for this Board / Grade / Subject.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Profile Edit Modal */}
      {isModalOpen && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.modal}>
            <button style={modalStyles.closeBtn} onClick={() => setIsModalOpen(false)}>&times;</button>
            <div style={modalStyles.userIcon}><FaUserCircle /></div>

            <div style={modalStyles.formGroup}>
              <label>Username</label>
              <input
                type="text"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Email</label>
              <input type="email" value={user.email} disabled style={{ ...modalStyles.input, background: '#f0f0f0', color: '#888' }} />
            </div>

            <div style={modalStyles.formGroup}>
              <label>Password</label>
              <input
                type="password"
                value={user.password}
                onChange={(e) => setUser({ ...user, password: e.target.value })}
                style={modalStyles.input}
              />
            </div>

            <div style={{ textAlign: 'right' }}>
              <button style={modalStyles.saveBtn} onClick={handleSaveProfile}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: '#fff', padding: '2rem', borderRadius: '12px', width: '90%', maxWidth: '400px', position: 'relative', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' },
  closeBtn: { position: 'absolute', top: '10px', right: '15px', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer' },
  userIcon: { textAlign: 'center', fontSize: '4rem', color: '#129255', marginBottom: '1rem' },
  formGroup: { marginBottom: '1rem' },
  input: { width: '100%', padding: '0.5rem 0.8rem', borderRadius: '6px', border: '1px solid #ccc', outline: 'none' },
  saveBtn: { background: '#129255', color: '#fff', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
};

export default Longansedit;
